/**
 * The public pages' broadsheet frame, shared by the landing page and the
 * comparison page: the masthead and a section head over the ink rule.
 */
import { Link } from "@tanstack/react-router";
import { dateline } from "../../lib/time.ts";
import { SignInLink } from "../sign-in.tsx";

/** The nameplate, the sign-in link, and the price line. `home` links the name to "/". */
export function Masthead({ home = false }: { home?: boolean }) {
  const name = (
    <span className="heading text-[28px] tracking-[-0.02em] md:text-[32px]">
      <span aria-hidden="true">ba·bi·a</span>
      <span className="sr-only">babia</span>
    </span>
  );
  return (
    <header>
      <div className="flex items-center gap-2">
        <p className="flex min-w-0 items-baseline gap-x-2 whitespace-nowrap">
          {home ? (
            <Link to="/" className="inline-flex min-h-11 items-center">
              {name}
            </Link>
          ) : (
            name
          )}
          <span
            aria-hidden="true"
            className="hidden text-[18px] italic opacity-70 min-[360px]:inline md:text-[20px]"
          >
            /ba-BEE-uh/
          </span>
          <span className="sr-only">, pronounced ba-bee-uh</span>
        </p>
        <SignInLink className="-mr-2 ml-auto shrink-0" />
      </div>
      <div className="mt-1.5 h-[3px] bg-ink" />
      <div className="kicker flex items-baseline gap-3 py-1 leading-none">
        <span className="min-w-0 truncate">Founding price: $1.99 once</span>
        <span className="ml-auto hidden shrink-0 min-[400px]:inline">{dateline(new Date())}</span>
      </div>
      <div className="h-px bg-ink opacity-90" />
    </header>
  );
}

/** A section of the page: a serif head over the ink rule, like the overview's. */
export function Section({
  id,
  title,
  note,
  children,
}: {
  id: string;
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-16 md:mt-20" aria-labelledby={id}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-ink pb-2">
        <h2 id={id} className="heading text-[30px] tracking-[-0.02em] md:text-[36px]">
          {title}
        </h2>
        {note ? <p className="text-[14px] italic opacity-65 md:ml-auto">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}
